import { Title } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler, LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import { DatePipe } from '@angular/common';
import { registerLocaleData } from '@angular/common';
import localeSv from '@angular/common/locales/sv';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalModule, MsalService, MsalRedirectComponent } from '@azure/msal-angular';
import { AppComponent } from './app.component';
import { NavBarComponent } from './nav-bar/nav-bar.component';
import { CounterComponent } from './counter/counter.component';
import { FetchDataComponent } from './fetch-data/fetch-data.component';
import { SchedulerComponent } from './scheduler/scheduler.component';
import { TooltipService } from './services/toolTip.service';
import { SchedulerModule } from './scheduler/scheduler.module';
import { ReportsModule } from './reports/reports.module';
import { SignalrErrorComponent } from './shared/signalr-error.component';
import { SignalrErrorDialogComponent } from './shared/signalr-error-dialog.component';
import { ToastrModule } from 'ngx-toastr';
import { MaterialModule } from './shared/material.module';
import { ServicesModule } from './services/services.module';
import { AuthorizeErrorDialogComponent } from './shared/authorize-error-dialog.component';
import { AuthorizeErrorComponent } from './shared/authorize-error.component';
import { UsersComponent } from './usrs/users.component';
import { CreateUserDialogComponent } from './usrs/create-user-dialog.component';
import { DeleteUserDialogComponent } from './usrs/delete-user-dialog.component';
import { EditUserDialogComponent } from './usrs/edit-user-dialog.component';
import { MessageDialogComponent } from './message/message-dialog.component';
import { NewMessageDialogComponent } from './message/new-message-dialog.component';
import { ConfirmDialog } from './confirmation/confirm.dialog';
import { TransferDialog } from './transfer/transfer.dialog';
import { DirectivesModule } from './directives/directives.module';
import { ReportsComponent } from './reports/reports.component';
import { SettingsService } from './services/settings.service';
import { GlobalErrorHandler } from './error-handler/globalErrorHandler';
import { AppInsightsService } from './services/appInsights.service';
import { NgxSpinnerModule } from '@hardpool/ngx-spinner';
import { AdministrationModule } from './administration/administration.module';
import { AdministrationComponent } from './administration/administration.component';
import { BusmapComponent } from './busmap/busmap.component';
import { GoogleMapsModule } from '@angular/google-maps';
import { CheckinComponent } from './checkin/checkin.component';
import { WorkScheduleComponent } from './work-schedule/work-schedule.component';
import { CreateWorkScheduleComponent } from './work-schedule/create-work-schedule.component';
import { EditWorkScheduleComponent } from './work-schedule/edit-work-schedule.component';
import { HeromaImporterComponent } from './work-schedule/heroma-importer.component';
import { LogDialogComponent } from './user-import-logs/log-dialog.component';
import { InactivityComponent } from './shared/inactivity.component';
import { InactivityDialogComponent } from './shared/inactivity-dialog.component';
import { DispatcherComponent } from './dispatcher/dispatcher.component';
import { AppGuard } from './appGuard';
import { AuthRoleGuard } from './services/auth.roleguard.service';
import { Roles } from './common/enums';
import { UnsavedChangesGuard } from './services/unsavedChangesGuard.service'
import { AppRoute } from './model/appRoute';

const routes: AppRoute[] = [
  { path: 'dispatcher', component: DispatcherComponent, canActivate: [AppGuard], data: { expectedRole: [Roles.Planner, Roles.Dispatcher], skipInactivityCheck: true } },
  { path: 'plan', component: SchedulerComponent, canActivate: [AppGuard], data: { expectedRole: [Roles.Planner] } },
  { path: 'busmap', component: BusmapComponent, canActivate: [AppGuard], data: { expectedRole: [Roles.Planner] } },
  { path: 'users', component: UsersComponent, canActivate: [AppGuard], data: { expectedRole: [Roles.Planner] } },
  { path: 'reports', component: ReportsComponent, canActivate: [AppGuard], data: { expectedRole: [Roles.Planner] } },
  { path: 'administration', component: AdministrationComponent, canActivate: [AppGuard], data: { expectedRole: [Roles.Planner] } },
  { path: 'schedule', component: WorkScheduleComponent, canActivate: [AppGuard], canDeactivate: [UnsavedChangesGuard], data: { expectedRole: [Roles.Planner] } },
  { path: 'id_token', redirectTo: '/plan' },
  { path: 'signalrerror', component: SignalrErrorComponent, canActivate: [AppGuard] },
  { path: 'inactivity', component: InactivityComponent, canActivate: [AppGuard] },
  { path: 'authorizeerror', component: AuthorizeErrorComponent, canActivate: [AppGuard] },
  { path: 'auth-logout', redirectTo: '/plan'},
  { path: 'checkin', component: CheckinComponent, canActivate: [AppGuard], data: {skipInactivityCheck: true } },
  { path: '', redirectTo: '', pathMatch: 'full', canActivate: [AppGuard] }
];

registerLocaleData(localeSv);

@NgModule({
  declarations: [
    AppComponent,
    NavBarComponent,
    CounterComponent,
    FetchDataComponent,
    BusmapComponent,
    SignalrErrorComponent,
    SignalrErrorDialogComponent,
    AuthorizeErrorComponent,
    AuthorizeErrorDialogComponent,
    UsersComponent,
    CreateUserDialogComponent,
    DeleteUserDialogComponent,
    EditUserDialogComponent,
    MessageDialogComponent,
    NewMessageDialogComponent,
    ConfirmDialog,
    TransferDialog,
    WorkScheduleComponent,
    CreateWorkScheduleComponent,
    EditWorkScheduleComponent,
    HeromaImporterComponent,
    LogDialogComponent,
    CheckinComponent,
    InactivityComponent,
    InactivityDialogComponent,
    DispatcherComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    HttpClientModule,
    DirectivesModule,
    FormsModule,
    ReactiveFormsModule,
    SchedulerModule,
    ReportsModule,
    AdministrationModule,
    NgxSpinnerModule,
    MaterialModule,
    ServicesModule,
    ReactiveFormsModule,
    GoogleMapsModule,
    ToastrModule.forRoot({
      timeOut: 10000,
      enableHtml:true,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true
    }),
    MsalModule,
    RouterModule.forRoot(routes, {
      useHash: false,
      onSameUrlNavigation: 'reload'
    })
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ],
  providers: [
    AuthRoleGuard,
    { provide: LOCALE_ID, useValue: "sv-SE" },
    DatePipe,
    TooltipService,
    GlobalErrorHandler,
    Title,
    AppInsightsService,
    SettingsService,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    MsalService,
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]

})
export class AppModule { }
